import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Flex,
  Button,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  SimpleGrid,
  useDisclosure,
  useToast,
  IconButton,
  Wrap,
  WrapItem,
  Select,
  InputGroup,
  InputLeftElement,
  Switch,
} from '@chakra-ui/react';
import { MdAdd, MdEdit, MdLock, MdDelete, MdSearch } from 'react-icons/md';
import Card from 'components/card/Card';
import UserFormModal from 'components/userManagement/UserFormModal';
import PermissionTreePicker from 'components/userManagement/PermissionTreePicker';
import { getPermissionLabel } from 'config/permissionTree';
import {
  getUsers,
  updateUser,
  resetUserPassword,
  getRoles,
  getPermissionTree,
  createRole,
  updateRole,
  deleteRole,
} from 'services/userManagement.service';

function formatDate(value) {
  if (!value) return 'Nunca';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Nunca';
  return date.toLocaleString('es-UY');
}

function ProfileBadges({ profiles, roles }) {
  return (
    <Wrap spacing={1}>
      {profiles?.student && (
        <WrapItem>
          <Badge colorScheme="green">Estudiante</Badge>
        </WrapItem>
      )}
      {profiles?.teacher && (
        <WrapItem>
          <Badge colorScheme="orange">Docente</Badge>
        </WrapItem>
      )}
      {(roles || []).map((r) => (
        <WrapItem key={r.id}>
          <Badge colorScheme="blue">{r.role}</Badge>
        </WrapItem>
      ))}
    </Wrap>
  );
}

export default function UserManagementView() {
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionTree, setPermissionTree] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [profileFilter, setProfileFilter] = useState('all');
  const [enabledFilter, setEnabledFilter] = useState('all');

  const userModal = useDisclosure();
  const roleModal = useDisclosure();
  const passwordModal = useDisclosure();

  const [editUserId, setEditUserId] = useState(null);
  const [roleForm, setRoleForm] = useState({ role: '', accessIds: [] });
  const [passwordForm, setPasswordForm] = useState('');
  const [editRoleId, setEditRoleId] = useState(null);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (profileFilter !== 'all') params.profile = profileFilter;
      if (enabledFilter !== 'all') params.enabled = enabledFilter;
      const usersRes = await getUsers(params);
      setUsers(usersRes?.payload?.data || []);
    } catch (error) {
      toast({
        title: 'Error al cargar usuarios',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [search, profileFilter, enabledFilter, toast]);

  const loadRolesAndPermissions = async () => {
    const [rolesRes, treeRes] = await Promise.all([getRoles(), getPermissionTree()]);
    setRoles(rolesRes?.payload?.data || []);
    setPermissionTree(treeRes?.payload?.data || []);
  };

  useEffect(() => {
    loadRolesAndPermissions();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    // Al vaciar el input se limpia el filtro sin necesidad de Enter
    if (value === '' && search !== '') setSearch('');
  };

  const openCreateUser = () => {
    setEditUserId(null);
    userModal.onOpen();
  };

  const openEditUser = (user) => {
    setEditUserId(user.id);
    userModal.onOpen();
  };

  const openResetPassword = (userId) => {
    setPasswordUserId(userId);
    setPasswordForm('');
    passwordModal.onOpen();
  };

  const openCreateRole = () => {
    setEditRoleId(null);
    setRoleForm({ role: '', accessIds: [] });
    loadRolesAndPermissions();
    roleModal.onOpen();
  };

  const openEditRole = (role) => {
    setEditRoleId(role.id);
    setRoleForm({
      role: role.role,
      accessIds: role.accesses?.map((a) => a.id) || [],
    });
    loadRolesAndPermissions();
    roleModal.onOpen();
  };

  const handleToggleEnabled = async (user) => {
    try {
      await updateUser(user.id, { enabled: !user.enabled });
      toast({
        title: user.enabled ? 'Acceso deshabilitado' : 'Acceso habilitado',
        status: 'success',
        duration: 2500,
      });
      loadUsers();
    } catch (error) {
      toast({
        title: 'Error al cambiar estado',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 4000,
      });
    }
  };

  const handleSavePassword = async () => {
    if (!passwordForm || passwordForm.length < 8) {
      toast({ title: 'La contraseña debe tener al menos 8 caracteres', status: 'warning', duration: 3000 });
      return;
    }
    try {
      setSaving(true);
      await resetUserPassword(passwordUserId, passwordForm);
      toast({ title: 'Contraseña actualizada', status: 'success', duration: 3000 });
      passwordModal.onClose();
    } catch (error) {
      toast({
        title: 'Error al cambiar contraseña',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRole = async () => {
    if (!roleForm.role.trim()) return;
    try {
      setSaving(true);
      if (editRoleId) {
        await updateRole(editRoleId, roleForm);
        toast({ title: 'Rol actualizado', status: 'success', duration: 3000 });
      } else {
        await createRole(roleForm);
        toast({ title: 'Rol creado', status: 'success', duration: 3000 });
      }
      roleModal.onClose();
      loadRolesAndPermissions();
    } catch (error) {
      toast({
        title: 'Error al guardar rol',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`¿Eliminar el rol "${role.role}"?`)) return;
    try {
      await deleteRole(role.id);
      toast({ title: 'Rol eliminado', status: 'success', duration: 3000 });
      loadRolesAndPermissions();
    } catch (error) {
      toast({
        title: 'No se pudo eliminar el rol',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 4000,
      });
    }
  };

  return (
    <Box>
      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="brand">
        <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
          <TabList>
            <Tab>Usuarios</Tab>
            <Tab>Roles y permisos</Tab>
          </TabList>
          <Button
            leftIcon={<MdAdd />}
            colorScheme="brand"
            size="sm"
            onClick={tabIndex === 0 ? openCreateUser : openCreateRole}
          >
            {tabIndex === 0 ? 'Nuevo usuario' : 'Nuevo rol'}
          </Button>
        </Flex>

        <TabPanels>
          <TabPanel px={0}>
            <Card mb={4} p={4}>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                <FormControl gridColumn={{ md: 'span 2' }}>
                  <FormLabel fontSize="sm">Buscar</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <MdSearch color="gray" />
                    </InputLeftElement>
                    <Input
                      placeholder="Nombre, documento, usuario, email... (Enter para buscar)"
                      value={searchInput}
                      onChange={handleSearchChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') setSearch(searchInput);
                      }}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Perfil</FormLabel>
                  <Select value={profileFilter} onChange={(e) => setProfileFilter(e.target.value)}>
                    <option value="all">Todos</option>
                    <option value="roles">Con rol asignado</option>
                    <option value="student">Estudiante</option>
                    <option value="teacher">Docente</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Estado de acceso</FormLabel>
                  <Select value={enabledFilter} onChange={(e) => setEnabledFilter(e.target.value)}>
                    <option value="all">Todos</option>
                    <option value="true">Activos</option>
                    <option value="false">Inactivos</option>
                  </Select>
                </FormControl>
              </SimpleGrid>
            </Card>

            <Card overflowX="auto">
              {!loading && (
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor}>Persona</Th>
                      <Th borderColor={borderColor}>Usuario</Th>
                      <Th borderColor={borderColor}>Perfiles</Th>
                      <Th borderColor={borderColor}>Último acceso</Th>
                      <Th borderColor={borderColor}>Acceso</Th>
                      <Th borderColor={borderColor}>Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {users.length === 0 ? (
                      <Tr>
                        <Td colSpan={6} borderColor={borderColor}>
                          <Text color="gray.500" textAlign="center" py={4}>
                            No se encontraron usuarios
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      users.map((user) => (
                        <Tr key={user.id} _hover={{ bg: rowHover }} opacity={user.enabled ? 1 : 0.65}>
                          <Td borderColor={borderColor}>
                            {user.person ? (
                              <>
                                <Text fontWeight="600" color={textColor}>
                                  {`${user.person.name} ${user.person.lastName || ''}`.trim()}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {user.person.document}
                                  {user.person.email ? ` · ${user.person.email}` : ''}
                                </Text>
                              </>
                            ) : (
                              <Badge colorScheme="red">Sin persona</Badge>
                            )}
                          </Td>
                          <Td borderColor={borderColor}>
                            <Text color={textColor}>{user.username}</Text>
                          </Td>
                          <Td borderColor={borderColor}>
                            <ProfileBadges profiles={user.profiles} roles={user.roles} />
                          </Td>
                          <Td borderColor={borderColor}>{formatDate(user.lastLoginDate)}</Td>
                          <Td borderColor={borderColor}>
                            <Switch
                              size="sm"
                              colorScheme="brand"
                              isChecked={user.enabled}
                              onChange={() => handleToggleEnabled(user)}
                            />
                          </Td>
                          <Td borderColor={borderColor}>
                            <HStack>
                              <IconButton
                                icon={<MdEdit />}
                                size="sm"
                                variant="ghost"
                                aria-label="Editar"
                                onClick={() => openEditUser(user)}
                              />
                              <IconButton
                                icon={<MdLock />}
                                size="sm"
                                variant="ghost"
                                aria-label="Cambiar contraseña"
                                onClick={() => openResetPassword(user.id)}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              )}
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            <Card overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor}>Rol</Th>
                    <Th borderColor={borderColor}>Usuarios</Th>
                    <Th borderColor={borderColor}>Permisos</Th>
                    <Th borderColor={borderColor}>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {roles.map((role) => (
                    <Tr key={role.id} _hover={{ bg: rowHover }}>
                      <Td borderColor={borderColor}>
                        <Text fontWeight="600" color={textColor}>{role.role}</Text>
                      </Td>
                      <Td borderColor={borderColor}>{role.usersCount}</Td>
                      <Td borderColor={borderColor}>
                        <Wrap spacing={1}>
                          {(role.accesses || []).map((a) => (
                            <WrapItem key={a.id}>
                              <Badge>{getPermissionLabel(a.access)}</Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Td>
                      <Td borderColor={borderColor}>
                        <HStack>
                          <IconButton
                            icon={<MdEdit />}
                            size="sm"
                            variant="ghost"
                            aria-label="Editar rol"
                            onClick={() => openEditRole(role)}
                          />
                          <IconButton
                            icon={<MdDelete />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            aria-label="Eliminar rol"
                            onClick={() => handleDeleteRole(role)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <UserFormModal
        isOpen={userModal.isOpen}
        onClose={userModal.onClose}
        editUserId={editUserId}
        roles={roles}
        onSaved={loadUsers}
      />

      <Modal isOpen={passwordModal.isOpen} onClose={passwordModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Restablecer contraseña</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Nueva contraseña</FormLabel>
              <Input
                type="password"
                value={passwordForm}
                onChange={(e) => setPasswordForm(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={passwordModal.onClose}>Cancelar</Button>
            <Button colorScheme="brand" onClick={handleSavePassword}>
              Actualizar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={roleModal.isOpen} onClose={roleModal.onClose} size="2xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>{editRoleId ? 'Editar rol' : 'Nuevo rol'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Nombre del rol</FormLabel>
                <Input
                  value={roleForm.role}
                  onChange={(e) => setRoleForm({ ...roleForm, role: e.target.value.toUpperCase() })}
                  placeholder="Ej: BEDELIA, CONTADOR"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Permisos por sección</FormLabel>
                <Text fontSize="sm" color="gray.500" mb={2}>
                  Marcá un módulo completo o solo las opciones que necesite este rol.
                </Text>
                <PermissionTreePicker
                  tree={permissionTree}
                  selectedIds={roleForm.accessIds}
                  onChange={(accessIds) => setRoleForm({ ...roleForm, accessIds })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={roleModal.onClose}>Cancelar</Button>
            <Button colorScheme="brand" onClick={handleSaveRole}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
